import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import type { TOC } from '@ember/component/template-only'
import { on } from '@ember/modifier'
import { service } from '@ember/service'
import { t, formatDate } from 'ember-intl'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { Log } from '@blakeelearning/log'
import { trackedFunction } from 'reactiveweb/function'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { Collapser } from 'teacher-ui-client/components/collapser'
import WidgetCard, { WidgetNoDataMessage } from 'teacher-ui-client/components/teacher-home/widget-card'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { camelize } from '@ember/string'

export interface RecentActivityResponse {
  data: StudentActivity[]
}

interface StudentActivity {
  student_id: number
  first_name: string
  last_name: string
  grade_name: string
  time_in_session: number
  events: Activity[]
}

interface Activity {
  type: string
  product: string
  passed?: boolean
  score?: number
  time_of_activity: string
}

interface Signature {
  Element: HTMLDivElement
}

export class RecentActivityWidget extends Component<Signature> {
  @service authToken!: AuthToken
  @service log!: Log
  @service subscriptionType!: SubscriptionTypeService

  fetchData = trackedFunction(this, async () => {
    try {
      const url = join(config.gravityV3Url, 'teacher-ui/widgets/recent-activity/', this.subscriptionType.current).href
      const headers = {
        Authorization: this.authToken.token ?? '',
        Accept: 'application/json',
      }
      const response = await fetch(url, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch recent activity data: ${response.status}`)
      }

      return response.json() as Promise<RecentActivityResponse>
    } catch (err) {
      this.log.error('failed to fetch recent activity data', err ?? undefined)
      throw err
    }
  })

  get studentData(): Nullable<StudentActivity[]> {
    return this.fetchData.value?.data ?? null
  }

  <template>
    <WidgetCard
      @title={{t "teacherHome.widgets.recentActivity.title"}}
      @borderAccentClass="border-t-forest-green-300"
      data-test-teacher-home-widget-card="recent-activity"
      ...attributes
    >
      {{#if this.fetchData.isPending}}
        <LoadingState />
      {{else if this.studentData}}
        <ul class="h-full overflow-y-auto">
          {{#each this.studentData as |student|}}
            <StudentRow @student={{student}} />
          {{/each}}
        </ul>
      {{else}}
        <WidgetNoDataMessage />
      {{/if}}
    </WidgetCard>
  </template>
}

interface StudentRowSignature {
  Element: HTMLLIElement
  Args: {
    student: StudentActivity
  }
}

class StudentRow extends Component<StudentRowSignature> {
  @tracked isOpen = false

  toggle = () => {
    this.isOpen = !this.isOpen
  }

  get fullName(): string {
    return `${this.args.student.first_name} ${this.args.student.last_name}`
  }

  get rowId(): string {
    return `recent-activity-${this.args.student.student_id}`
  }

  get activityCount(): number {
    return this.args.student.events.length
  }

  get passedCount(): number {
    return this.args.student.events.filter((event) => event.passed === true).length
  }

  get attemptedCount(): number {
    return this.args.student.events.filter((event) => event.passed === false).length
  }

  <template>
    <li
      class="border-dusty-black-100 h-1/5 [&:not(:nth-of-type(5):last-of-type)]:border-b"
      data-test-recent-activity-student-row={{@student.student_id}}
    >
      <button
        class="flex h-full w-full items-center px-2.5 transition-colors hover:bg-neutral-50/70"
        type="button"
        data-test-recent-activity-student-toggle={{@student.student_id}}
        aria-expanded={{if this.isOpen "true" "false"}}
        aria-controls="{{this.rowId}}-content"
        {{on "click" this.toggle}}
      >
        <div class="flex-1 text-left">
          <p class="truncate text-sm font-semibold text-gray-900">{{this.fullName}}</p>
          <p class="text-xs leading-tight text-gray-400">{{@student.grade_name}}</p>
        </div>
        <div class="flex items-center gap-1.5">
          <div class="flex flex-col items-end gap-1 text-xs leading-none font-semibold">
            <span class="bg-oceany-blue-100 text-oceany-blue-325 rounded-full px-1.5 py-0.5">
              {{t "teacherHome.widgets.recentActivity.badges.activitiesWithCount" count=this.activityCount}}
            </span>
            {{#if this.passedCount}}
              <span class="bg-ms-green-350/10 text-ms-green-350 rounded-full px-1.5 py-0.5">
                {{t "teacherHome.widgets.recentActivity.badges.passedWithCount" count=this.passedCount}}
              </span>
            {{/if}}
            {{#if this.attemptedCount}}
              <span class="bg-juicy-orange-400/10 text-juicy-orange-400 rounded-full px-1.5 py-0.5">
                {{t "teacherHome.widgets.recentActivity.badges.attemptedWithCount" count=this.attemptedCount}}
              </span>
            {{/if}}
          </div>
          <PhosphorIcon
            @icon="phosphor-caret-down-bold"
            class="h-5 w-5 text-gray-400 transition-transform duration-200 ease-out {{if this.isOpen 'rotate-180' ''}}"
            aria-hidden="true"
          />
        </div>
      </button>
    </li>
    <Collapser
      id="{{this.rowId}}-content"
      @open={{this.isOpen}}
      class={{if this.isOpen "border-dusty-black-100 [&:nth-of-type(5):last-of-type]:border-t"}}
      data-test-recent-activity-collapser
    >
      <ul class="bg-neutral-50/40">
        {{#each @student.events as |activity|}}
          <ActivityRow @activity={{activity}} />
        {{else}}
          <ActivityRow />
        {{/each}}
      </ul>
    </Collapser>
  </template>
}

interface ActivityRowSignature {
  Element: HTMLLIElement
  Args: {
    activity?: Activity
  }
}

class ActivityRow extends Component<ActivityRowSignature> {
  get activityTranslation() {
    if (!this.args.activity) return 'teacherHome.widgets.recentActivity.events.noEvents'
    return `teacherHome.widgets.recentActivity.events.${camelize(this.args.activity.type)}`
  }

  get badgeText(): string {
    if (!this.args.activity) return ''
    if (this.args.activity.passed === true) return 'teacherHome.widgets.recentActivity.badges.passed'
    if (this.args.activity.passed === false) return 'teacherHome.widgets.recentActivity.badges.attempted'
    return 'teacherHome.widgets.recentActivity.badges.completed'
  }

  get badgeClass(): string {
    if (!this.args.activity) return ''
    if (this.args.activity.passed === true) return 'bg-ms-green-350/10 text-ms-green-350'
    if (this.args.activity.passed === false) return 'bg-juicy-orange-400/10 text-juicy-orange-400'
    return 'bg-oceany-blue-100 text-oceany-blue-325'
  }

  <template>
    <li class="border-dusty-black-100 flex h-12 items-center border-b px-3.5" data-test-recent-activity-event-row>
      <p class="flex-1 text-xs font-medium text-neutral-400">
        {{t this.activityTranslation}}
      </p>
      {{#if @activity}}
        <div class="space-x-1 text-xs">
          <span
            class="rounded px-1.5 py-0.5 text-[10px] font-semibold {{this.badgeClass}}"
            data-test-recent-activity-event-badge
          >
            {{t this.badgeText}}
          </span>
          <span class="text-neutral-250">
            {{formatDate @activity.time_of_activity weekday="short" hour="numeric" minute="2-digit" hour12=true}}
          </span>
        </div>
      {{/if}}
    </li>
  </template>
}

const LoadingState: TOC<Signature> = <template>
  <div class="divide-dusty-black-100 animate-pulse divide-y" data-test-recent-activity-loading-state>
    <LoadingRow />
    <LoadingRow />
    <LoadingRow />
    <LoadingRow />
    <LoadingRow />
  </div>
</template>

const LoadingRow: TOC<Signature> = <template>
  <div class="flex h-18.25 w-full items-center px-2.5" aria-hidden="true" data-test-recent-activity-loading-row>
    <div class="flex-1 text-left">
      <div class="bg-dusty-black-50 h-4 w-48 animate-pulse rounded"></div>
      <div class="bg-dusty-black-50 mt-1 h-3 w-8 animate-pulse rounded"></div>
    </div>
    <div class="flex items-center gap-1.5">
      <div class="bg-dusty-black-50 inline-flex h-4.5 w-16 animate-pulse rounded-full"></div>
      <PhosphorIcon @icon="phosphor-caret-down-bold" class="text-dusty-black-100 h-5 w-5 animate-pulse" />
    </div>
  </div>
</template>

export default RecentActivityWidget
